import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const stats = [
  { value: '27+', label: 'Years of Excellence', icon: 'fa-star', color: 'bg-primary/10 text-primary' },
  { value: '3', label: 'Campus Locations', icon: 'fa-building', color: 'bg-accent/10 text-accent' },
  { value: '1600+', label: 'Happy Students', icon: 'fa-users', color: 'bg-green-100 text-green-600' },
  { value: '120+', label: 'Expert Teachers', icon: 'fa-chalkboard-teacher', color: 'bg-purple-100 text-purple-600' },
];

const features = [
  { icon: 'fa-medal', label: 'Academic Excellence', color: 'text-primary' },
  { icon: 'fa-heart', label: 'Character Building', color: 'text-accent' },
  { icon: 'fa-laptop', label: 'Modern Facilities', color: 'text-green-600' },
  { icon: 'fa-users', label: 'Expert Faculty', color: 'text-purple-600' },
];

const AboutSection = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Image Column */}
          <div className={`relative transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative">
              {/* Main image */}
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/assets/about-image.jpg"
                  alt="PAK School & College campus"
                  className="w-full h-80 sm:h-96 lg:h-[420px] object-cover"
                  loading="lazy"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
                    <i className="fas fa-award text-primary" />
                    <span className="font-poppins font-600 text-navy text-sm">27+ Years of Excellence</span>
                  </div>
                </div>
              </div>

              {/* Floating stats card */}
              <div className="absolute -bottom-6 -right-6 sm:-right-8 bg-white rounded-2xl shadow-xl p-5 border border-gray-100 w-44">
                <div className="text-center">
                  <div className="font-poppins font-800 text-3xl text-primary">1600+</div>
                  <div className="text-gray-500 text-xs mt-1 font-inter">Students Enrolled</div>
                  <div className="flex justify-center mt-2 gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="fas fa-star text-yellow-400 text-xs" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Decorative blob */}
              <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-primary/10 -z-10" />
              <div className="absolute top-1/2 -left-4 w-16 h-16 rounded-full bg-accent/10 -z-10" />
            </div>
          </div>

          {/* Content Column */}
          <div className={`space-y-6 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div>
              <div className="section-badge">
                <i className="fas fa-school" />
                About PAK School
              </div>
              <h2 className="section-title">
                Nurturing Excellence,
                <br />
                <span className="gradient-text">Building Bright Futures</span>
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mt-4">
                At PAK School and College System, we pride ourselves on providing a dynamic learning environment that nurtures academic excellence and personal development. With a dedicated faculty and commitment to fostering creativity, critical thinking, and character building, we strive to empower every student.
              </p>
              <p className="text-gray-500 text-base leading-relaxed mt-3">
                Our holistic approach to education ensures a well-rounded learning experience, preparing students for a future of success and positive contributions to society.
              </p>
            </div>

            {/* Feature chips */}
            <div className="grid grid-cols-2 gap-3">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <i className={`fas ${f.icon} ${f.color} w-5 text-center`} />
                  <span className="text-sm font-inter font-600 text-gray-700">{f.label}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/about" className="btn-primary">
                Learn More
                <i className="fas fa-arrow-right" />
              </Link>
              <Link to="/admission" className="btn-outline">
                Apply Now
              </Link>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className={`mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {stats.map((stat, i) => (
            <div key={i} className="group bg-white border border-gray-100 rounded-2xl p-6 text-center shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`fas ${stat.icon} text-lg`} />
              </div>
              <div className="font-poppins font-800 text-3xl text-navy mb-1">{stat.value}</div>
              <div className="text-sm text-gray-500 font-inter">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
